// The overworld camera's input and ease (docs/42 on the World screen; geometry in `overworldCamera.ts`).
// The map element takes the pointer (drags, the wheel, pinches); the window takes the keys and the
// mouse's place for edge panning. The stage is moved by a CSS transform written each frame straight
// to the element — no React render — and starts exactly where the CSS layout put it (z 1).
import { useEffect, useRef, type RefObject } from 'react';
import { EdgePan, edgePush, wheelZoomFactor, ZOOM_KEY_RATE, ZOOM_STEP } from '../../../game/board/BoardCamera';
import { useSettings } from '../../../core/stores/settings';
import { motionReduced } from '../../components';
import {
  clampOwCam, clampLockedCam, clampOwZoom, lockedMinZoom, easeOwCam, flingOffset, owMinZoom, owToStage, owTransform, typingIn, zoomOwAbout, OW_ZOOM_MAX,
  OW_DRAG_SLOP, OW_EASE, OW_EDGE_BAND, OW_EDGE_BLOCKERS, type OwCam,
} from './overworldCamera';
import { CAMERA_HOLD_IDS, CAMERA_PAN, CAMERA_ZOOM, hotkeyFor } from '../../../core/input/hotkeys';

type Anchor = { sx: number; sy: number; px: number; py: number };
type Pt = { x: number; y: number };
/** docs/33 E6: held-key and edge pans move the chart this many CSS px a second. */
const PAN_SPEED = 720;
/** How long (s) the stage stays its own layer after the camera settles, so a run of wheel notches never repaints between them. */
const LAYER_HOLD = 0.2;
/** docs/87: the chart's camera hotkeys (held pans and zooms, a step of + / −, V home). */
const OW_KEY_IDS = [...CAMERA_HOLD_IDS, 'camera.zoomIn', 'camera.zoomOut', 'camera.fit'];

/** docs/64 VP1: the village uses the same camera. `skip` is a selector for panels over the map whose own wheel and drags are theirs; a new `resetKey` (another room) starts the camera afresh. */
export interface OwCameraOptions {
  skip?: string; resetKey?: string;
  /** docs/185 PB13: write the camera to the map element as `--ow-cam-x`, `--ow-cam-y` (CSS px) and `--ow-cam-z`, for
   *  layers over the map that must line up with the stage (the weather kept off the Underdark). */
  vars?: boolean;
  /** docs/113: home frames this box (stage px) in the map less these insets (map px), zoomed out to fit (never in past 1). */
  fit?: { box: { x0: number; y0: number; x1: number; y1: number }; inset: { l: number; r: number; t: number; b: number } };
  /** docs/120 VM5: home zooms the stage (one size for every player) to cover the view's width (`w`, never
   * below 1) or the whole view (`wh`), standing on the view's bottom, with `focus` (stage px, default its
   * middle) as near the middle as the edges allow. */
  cover?: {
    axes: 'w' | 'wh'; focus?: { x: number };
    /** docs/121 VW7: home covers this span of the stage's width (stage px) rather than all of it. */
    frame?: { x0: number; x1: number };
    /** docs/121 VW7: the camera never shows past the stage's sides or below its foot, nor more than
     * `above` stage px over its top; it zooms out no further than the stage's whole width. */
    lock?: { above: number };
  };
}

export function useOverworldCamera(mapRef: RefObject<HTMLDivElement | null>, stageRef: RefObject<HTMLDivElement | null>, opts: OwCameraOptions = {}): void {
  const { skip, resetKey, vars = false } = opts;
  const fitKey = opts.fit ? JSON.stringify(opts.fit) : '';
  // the focus is read when home is taken (a new room, a resize, V), so a band walking about never resets the camera
  const coverRef = useRef(opts.cover);
  coverRef.current = opts.cover;
  const coverKey = opts.cover ? `${opts.cover.axes}:${opts.cover.frame ? `${opts.cover.frame.x0}-${opts.cover.frame.x1}` : ''}:${opts.cover.lock?.above ?? ''}` : '';
  useEffect(() => {
    const fit = fitKey ? (JSON.parse(fitKey) as NonNullable<OwCameraOptions['fit']>) : undefined;
    const map = mapRef.current, stage = stageRef.current;
    if (!map || !stage) return;
    const base = () => ({ x: stage.offsetLeft, y: stage.offsetTop });
    const chart = () => ({ w: stage.offsetWidth, h: stage.offsetHeight });
    const view = () => ({ w: map.clientWidth, h: map.clientHeight });
    const home = (): OwCam => {
      const cover = coverKey ? coverRef.current : undefined;
      if (cover) {
        const v = view(), c = chart();
        const fw = cover.frame ? cover.frame.x1 - cover.frame.x0 : c.w;
        const z0 = Math.min(OW_ZOOM_MAX, cover.axes === 'wh' ? Math.max(v.w / c.w, v.h / c.h) : Math.max(1, v.w / fw));
        const z = cover.lock ? Math.max(z0, lockedMinZoom(c, v)) : z0;
        const fx = cover.focus?.x ?? (cover.frame ? (cover.frame.x0 + cover.frame.x1) / 2 : c.w / 2);
        // a frame keeps home inside it; the stage's own edges bound it otherwise
        const lo = cover.frame ? Math.max(v.w - c.w * z, v.w - cover.frame.x1 * z) : v.w - c.w * z;
        const hi = cover.frame ? Math.min(0, -cover.frame.x0 * z) : 0;
        const x = Math.min(hi, Math.max(lo, v.w / 2 - fx * z));
        return { x: c.w * z <= v.w ? (v.w - c.w * z) / 2 : x, y: v.h - c.h * z, z };
      }
      if (!fit) return { ...base(), z: 1 };
      const v = view(), { box, inset } = fit;
      const aw = Math.max(1, v.w - inset.l - inset.r), ah = Math.max(1, v.h - inset.t - inset.b);
      const z = clampOwZoom(Math.min(1, aw / Math.max(1, box.x1 - box.x0), ah / Math.max(1, box.y1 - box.y0)), owMinZoom(chart(), v));
      return { x: inset.l + aw / 2 - ((box.x0 + box.x1) / 2) * z, y: inset.t + ah / 2 - ((box.y0 + box.y1) / 2) * z, z };
    };
    let cur = home(), goal = { ...cur };
    let anchor: Anchor | undefined;
    let easing: keyof typeof OW_EASE = 'pan';
    // until the player moves it the chart keeps the designed framing, through resizes too
    let touched = false;
    let press: { id: number; sx: number; sy: number; gx: number; gy: number; moved: boolean; trail: Array<{ t: number; x: number; y: number }> } | undefined;
    let swallowClick = false;
    const touches = new Map<number, Pt>();
    let pinch: { d0: number; z0: number; px: number; py: number } | undefined;
    let gesture: { z0: number } | undefined;
    const held = new Map<string, string>();   // docs/87: physical key → the camera hotkey it holds
    let edgeAt: (Pt & { ok: boolean }) | undefined;
    const edge = new EdgePan();
    let shown = '';

    const skipped = (t: EventTarget | null) => !!skip && !!(t as Element | null)?.closest?.(skip);
    const local = (cx: number, cy: number): Pt => { const b = map.getBoundingClientRect(); return { x: cx - b.left, y: cy - b.top }; };
    const locked = () => (coverKey ? coverRef.current?.lock : undefined);
    const minZoom = () => (locked() ? lockedMinZoom(chart(), view()) : owMinZoom(chart(), view()));
    const modalOpen = () => !!document.querySelector('[aria-modal="true"]');
    const clampGoal = () => {
      const lock = locked();
      const c = lock ? clampLockedCam(goal, chart(), view(), lock.above) : clampOwCam(goal, chart(), view());
      if (anchor && (c.x !== goal.x || c.y !== goal.y)) anchor = undefined;
      goal = c;
    };
    /** docs/42: scale the zoom by `factor` about `at` (map px; default: the view's centre). */
    const zoomAt = (factor: number, at: Pt | undefined, how: keyof typeof OW_EASE) => {
      const z = clampOwZoom(goal.z * factor, minZoom());
      if (z === goal.z) return;
      touched = true; easing = how;
      const v = view();
      const sx = at?.x ?? v.w / 2, sy = at?.y ?? v.h / 2;
      if (!anchor || at) { const p = owToStage(cur, { x: sx, y: sy }); anchor = { sx, sy, px: p.x, py: p.y }; }
      // the anchored chart point lands back under the pointer at the new zoom
      goal = zoomOwAbout({ x: anchor.sx - anchor.px * goal.z, y: anchor.sy - anchor.py * goal.z, z: goal.z }, z, { x: anchor.sx, y: anchor.sy });
      clampGoal();
    };
    const goHome = () => { touched = false; anchor = undefined; easing = 'zoom'; goal = home(); };

    // docs/42 CM1–CM2: the wheel and a trackpad pinch (ctrl+wheel) zoom about the pointer; over the
    // panels the wheel scrolls them, and a pinch there is swallowed so it never zooms the page
    const onWheel = (e: WheelEvent) => {
      if (!map.contains(e.target as Node) || skipped(e.target)) { if (e.ctrlKey) e.preventDefault(); return; }
      e.preventDefault();
      if (e.deltaY === 0 || gesture) return;
      zoomAt(wheelZoomFactor(e), local(e.clientX, e.clientY), 'zoom');
    };
    type GestureEv = Event & { scale: number; clientX: number; clientY: number };
    const onGestureStart = (e: Event) => { e.preventDefault(); if (touches.size < 2) gesture = { z0: goal.z }; };
    const onGestureChange = (e: Event) => {
      e.preventDefault();
      const g = e as GestureEv;
      if (!gesture) return;
      zoomAt(clampOwZoom(gesture.z0 * g.scale, minZoom()) / goal.z, local(g.clientX, g.clientY), 'pinch');
    };
    const onGestureEnd = (e: Event) => { e.preventDefault(); gesture = undefined; };

    // docs/33 E1: press and release without travelling is a click (a node, a band); a drag pans and
    // swallows the click it would end in. docs/42 CM4: two fingers pinch about their midpoint.
    const mid = () => { const [a, b] = [...touches.values()]; return { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2, d: Math.max(1, Math.hypot(a!.x - b!.x, a!.y - b!.y)) }; };
    const onDown = (e: PointerEvent) => {
      if (skipped(e.target)) return;
      const at = local(e.clientX, e.clientY);
      if (e.pointerType === 'touch') {
        touches.set(e.pointerId, at);
        if (touches.size === 2) {
          // a second finger cancels the first one's click or drag
          if (press) swallowClick = true;
          press = undefined;
          const m = mid(), p = owToStage(cur, m);
          pinch = { d0: m.d, z0: cur.z, px: p.x, py: p.y };
          return;
        }
      }
      if (e.button !== 0 || pinch) return;
      press = { id: e.pointerId, sx: at.x, sy: at.y, gx: goal.x, gy: goal.y, moved: false, trail: [{ t: e.timeStamp, ...at }] };
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') { const t = e.target as Element | null; edgeAt = e.buttons === 0 ? { x: e.clientX, y: e.clientY, ok: !t?.closest?.(OW_EDGE_BLOCKERS) } : undefined; }
      if (touches.has(e.pointerId)) {
        touches.set(e.pointerId, local(e.clientX, e.clientY));
        if (pinch && touches.size >= 2) {
          const m = mid(), z = clampOwZoom(pinch.z0 * (m.d / pinch.d0), minZoom());
          touched = true; easing = 'pinch';
          anchor = { sx: m.x, sy: m.y, px: pinch.px, py: pinch.py };
          goal = { x: m.x - pinch.px * z, y: m.y - pinch.py * z, z };
          clampGoal();
          return;
        }
      }
      if (!press || press.id !== e.pointerId) return;
      const at = local(e.clientX, e.clientY);
      if (!press.moved && Math.hypot(at.x - press.sx, at.y - press.sy) > OW_DRAG_SLOP) press.moved = true;
      if (!press.moved) return;
      touched = true; anchor = undefined; easing = 'drag';
      goal = { x: press.gx + (at.x - press.sx), y: press.gy + (at.y - press.sy), z: goal.z };
      clampGoal();
      press.trail.push({ t: e.timeStamp, ...at });
      while (press.trail.length > 2 && e.timeStamp - press.trail[0]!.t > 100) press.trail.shift();
    };
    const onUp = (e: PointerEvent) => {
      touches.delete(e.pointerId);
      if (touches.size < 2) pinch = undefined;
      if (!press || press.id !== e.pointerId) return;
      const p = press;
      press = undefined;
      if (!p.moved) return;
      swallowClick = true;
      setTimeout(() => { swallowClick = false; }, 0);
      if (e.type === 'pointercancel' || motionReduced()) return;
      const [dx, dy] = flingOffset(p.trail, e.timeStamp);
      if (dx || dy) { easing = 'pan'; goal = { ...goal, x: goal.x + dx, y: goal.y + dy }; clampGoal(); }
    };
    const onClickCapture = (e: MouseEvent) => { if (swallowClick) { swallowClick = false; e.stopPropagation(); e.preventDefault(); } };
    const onDragStart = (e: Event) => e.preventDefault();

    // docs/33 E6 / docs/42 CM3: WASD and the arrows pan while held, Q zooms out and E in (a tap is a
    // nudge, holding keeps going), + / − a whole step, V back to the chart's own framing (default
    // keys, rebindable, docs/87). Never while typing (the dock's chat) or under a modal.
    const onKey = (e: KeyboardEvent) => {
      const id = hotkeyFor(e, OW_KEY_IDS);
      if (!id || typingIn(e.target) || modalOpen()) return;
      e.preventDefault();
      if (id === 'camera.fit') { if (!e.repeat) goHome(); return; }
      if (id === 'camera.zoomIn' || id === 'camera.zoomOut') { zoomAt(ZOOM_STEP ** (id === 'camera.zoomIn' ? 1 : -1), undefined, 'zoom'); return; }
      if (CAMERA_ZOOM[id] && !held.has(e.code)) zoomAt(ZOOM_KEY_RATE ** (CAMERA_ZOOM[id]! * 0.12), undefined, 'zoom');
      held.set(e.code, id);
    };
    const onKeyUp = (e: KeyboardEvent) => { held.delete(e.code); };
    const onBlur = () => { held.clear(); edgeAt = undefined; };
    const onLeave = (e: MouseEvent) => { if (!e.relatedTarget) edgeAt = undefined; };
    const onResize = () => { if (!touched) { cur = home(); goal = { ...cur }; } else { goal = { ...goal, z: clampOwZoom(goal.z, minZoom()) }; clampGoal(); } };

    /** docs/42 CM5: the push of a mouse resting in the edge band (none over a control, under a modal, or with the setting off). */
    const edgePushNow = (): [number, number] => {
      if (!edgeAt?.ok || press || pinch || !useSettings.getState().edgePan || document.hidden) return [0, 0];
      const push = edgePush(edgeAt.x, edgeAt.y, window.innerWidth, window.innerHeight, OW_EDGE_BAND);
      return (push[0] || push[1]) && modalOpen() ? [0, 0] : push;
    };

    let raf = 0, last = performance.now();
    let lifted = false, restFor = 0;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      const dt = Math.min(250, Math.max(0, now - last)) / 1000;
      last = now;
      let zoomDir = 0, dx = 0, dy = 0;
      for (const id of held.values()) { zoomDir += CAMERA_ZOOM[id] ?? 0; const d = CAMERA_PAN[id]; if (d) { dx += d[0]; dy += d[1]; } }
      if (zoomDir) zoomAt(ZOOM_KEY_RATE ** (zoomDir * dt), undefined, 'zoom');
      const n = Math.hypot(dx, dy) || 1;
      const [ex, ey] = edge.step(dt, edgePushNow());
      dx = Math.max(-1, Math.min(1, dx / n + ex)); dy = Math.max(-1, Math.min(1, dy / n + ey));
      if (dx || dy) {
        // the camera looks the way of the key, so the chart slides the other way
        touched = true; anchor = undefined; easing = 'pan';
        goal = { ...goal, x: goal.x - dx * PAN_SPEED * dt, y: goal.y - dy * PAN_SPEED * dt };
        clampGoal();
      }
      const moving = cur.x !== goal.x || cur.y !== goal.y || cur.z !== goal.z;
      if (moving) {
        const ease = (tc: number) => (motionReduced() ? 1 : 1 - Math.exp(-dt / tc));
        const r = easeOwCam(cur, goal, anchor, ease(easing === 'pinch' ? OW_EASE.pinch : OW_EASE.zoom), ease(OW_EASE[easing]));
        cur = r.cam;
        if (r.anchorDone) anchor = undefined;
      }
      // in motion the stage is its own layer, so the compositor scales one picture of it instead of
      // re-painting the canvases, blends and filters at every new zoom (which tears and makes the
      // pixelated cells crawl); a moment after it settles it drops back and repaints crisp
      if (moving) { lifted = true; restFor = 0; stage.style.willChange = 'transform'; }
      else if (lifted && (restFor += dt) > LAYER_HOLD) { lifted = false; stage.style.willChange = ''; }
      const tf = owTransform(cur, base(), window.devicePixelRatio || 1, !moving);
      if (tf !== shown) {
        shown = tf;
        stage.style.transform = tf;
        stage.dataset.camZoom = cur.z.toFixed(3);
        if (vars) { map.style.setProperty('--ow-cam-x', `${cur.x}px`); map.style.setProperty('--ow-cam-y', `${cur.y}px`); map.style.setProperty('--ow-cam-z', String(cur.z)); }
      }
    };
    raf = requestAnimationFrame(frame);

    const ro = new ResizeObserver(onResize);
    ro.observe(map);
    map.style.touchAction = 'none';
    const opts = { passive: false } as const;
    map.addEventListener('pointerdown', onDown);
    map.addEventListener('click', onClickCapture, true);
    map.addEventListener('dragstart', onDragStart);
    map.addEventListener('gesturestart', onGestureStart, opts); map.addEventListener('gesturechange', onGestureChange, opts); map.addEventListener('gestureend', onGestureEnd, opts);
    window.addEventListener('wheel', onWheel, opts);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp); window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey); window.addEventListener('keyup', onKeyUp); window.addEventListener('blur', onBlur);
    document.addEventListener('mouseout', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      map.removeEventListener('pointerdown', onDown);
      map.removeEventListener('click', onClickCapture, true);
      map.removeEventListener('dragstart', onDragStart);
      map.removeEventListener('gesturestart', onGestureStart); map.removeEventListener('gesturechange', onGestureChange); map.removeEventListener('gestureend', onGestureEnd);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp); window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKeyUp); window.removeEventListener('blur', onBlur);
      document.removeEventListener('mouseout', onLeave);
      stage.style.transform = '';
      stage.style.willChange = '';
    };
  }, [mapRef, stageRef, skip, resetKey, fitKey, coverKey, vars]);
}
